import { useMemo } from 'react';

import { useRouter } from 'next/router';

import { buildMasterProductGroupCodeObjects } from '~/shared/utils/buildMasterProductGroupCodeObjects';

import { mergeUnifiedPrimaryProductsAndRelatedData } from '../transformers/mergeUnifiedPrimaryProductsAndRelatedData';
import { NormalizedProductsV2InfiniteData } from '../transformers/normalizeProductsV2Response';
import { ProductsV2SortOption } from '../types/filters';
import { GridProviderResponse } from '../types/gridResponses';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useFacetTracking from './useFacetTracking';
import useProductsV2 from './useProductsV2';

type ProductsV2ProductsResponse = {
  data: NormalizedProductsV2InfiniteData<any>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  customFacetFilters: Record<string, any>;
  setCustomFacetFilters: (filters: Record<string, any>) => void;
};

const useProductsV2AndGqlProducts = (): GridProviderResponse => {
  const {
    data: productsV2Results,
    isLoading: isPrimaryProductsLoading,
    isFetching: isPrimaryProductsFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    customFacetFilters,
    setCustomFacetFilters,
  } = useProductsV2() as ProductsV2ProductsResponse;

  const pages = productsV2Results?.pages;
  const primaryProducts = productsV2Results?.products;

  const masterProductIds = useMemo(() => {
    return pages
      ?.flatMap((page) => page?.initialProducts?.products)
      ?.map((result) => result?.masterProductId);
  }, [pages]);

  // dedupe by groupCode, merging masterProductIds
  const groupCodeObjects = useMemo(
    () =>
      buildMasterProductGroupCodeObjects(
        pages?.flatMap((page) => page?.initialProducts?.products ?? []) ?? []
      ),
    [pages]
  );

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts(groupCodeObjects);

  // Combine the primary products with the pricingData and relatedProductsData
  const products = useMemo(
    () =>
      mergeUnifiedPrimaryProductsAndRelatedData({
        primaryProducts,
        pricingData,
        relatedProductsData,
      }),
    [primaryProducts, pricingData, relatedProductsData]
  );

  const firstPage = pages?.[0];
  const totalNumResults = firstPage?.totalNumResults;

  const rawFacets = productsV2Results?.facets;
  const facets = useMemo(() => {
    return (rawFacets || []).map((facet) => ({
      ...facet,
      options: facet?.options?.map((option) => ({
        ...option,
        value: String(option?.value),
      })),
    }));
  }, [rawFacets]);

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const urlParams = new URLSearchParams(query);
  const defaultSortOption = firstPage?.initialSortOption?.value;
  const selectedSortOption = urlParams.get('sort') || defaultSortOption;

  // TODO: move into transform function
  const sortOptions = useMemo(() => {
    return firstPage?.sortOptions?.map((sortOption: ProductsV2SortOption) => ({
      display_name: sortOption?.defaultMessage,
      // hidden,
      sort_by: sortOption?.value,
      sort_order:
        typeof sortOption?.sort === 'string'
          ? sortOption?.sort
          : sortOption?.sort?.direction || 'desc',
      status: selectedSortOption === sortOption?.value ? 'selected' : '',
    }));
  }, [firstPage, selectedSortOption]);

  const allRegisteredFacets = facets;
  const allRegisteredFacetNames = allRegisteredFacets?.map(
    (facet) => facet?.name
  );
  const selectedUrlFilters =
    firstPage?.productRequestParams?.[0]?.aggregationFilter || {};

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  return {
    type: 'products-v2' as const,
    products,
    fetchNextPage,
    hasNextPage,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    isPrimaryProductsLoading,
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useProductsV2AndGqlProducts;
